import React, { useState, useEffect } from 'react';
import { Battery, Flame, Moon, Clock, Activity, Zap } from 'lucide-react';
import StatCard from '../common/StatCard';
import InputCard from '../common/InputCard';
import TipCard from '../common/TipCard';
import { motion } from 'framer-motion';

const BatteryHealthPredictor = () => {
  const [chargesPerDay, setChargesPerDay] = useState(1.5);
  const [chargingHabit, setChargingHabit] = useState("normal");
  const [deviceAge, setDeviceAge] = useState(12);
  const [heatExposure, setHeatExposure] = useState("low");
  const [batteryHealth, setBatteryHealth] = useState(100);
  const [lifespanRemaining, setLifespanRemaining] = useState(24);

  useEffect(() => {
    let health = 100;
    health -= chargesPerDay * 0.08 * (deviceAge / 12);
    
    const habitImpact = { optimal: 0, normal: -5, overnight: -12, overcharge: -18 };
    health += habitImpact[chargingHabit] || 0;
    
    const heatImpact = { low: 0, medium: -10, high: -22 };
    health += heatImpact[heatExposure] || 0;
    
    health -= deviceAge / 6;
    
    const finalHealth = Math.min(100, Math.max(0, Math.round(health)));
    setBatteryHealth(finalHealth);
    setLifespanRemaining(Math.round((finalHealth / 100) * 42));
  }, [chargesPerDay, chargingHabit, deviceAge, heatExposure]);

  const getHealthColor = () => {
    if (batteryHealth >= 70) return { text: "text-green-600", bg: "bg-green-100", label: "Good" };
    if (batteryHealth >= 40) return { text: "text-yellow-600", bg: "bg-yellow-100", label: "Moderate" };
    return { text: "text-red-600", bg: "bg-red-100", label: "Poor" };
  };

  const healthStatus = getHealthColor();

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <InputCard title="Battery Usage Factors" icon={Battery} delay={0.1}>
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Charges per Day</label>
              <input type="range" min="0.5" max="4" step="0.1" value={chargesPerDay} onChange={(e) => setChargesPerDay(parseFloat(e.target.value))} className="w-full" />
              <div className="flex justify-between text-sm mt-1"><span>Light use</span><span className="font-semibold text-blue-600">{chargesPerDay}x daily</span><span>Heavy use</span></div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Charging Habit</label>
              <select value={chargingHabit} onChange={(e) => setChargingHabit(e.target.value)} className="w-full px-4 py-2.5 border rounded-lg">
                <option value="optimal">Optimal (20-80% charging)</option>
                <option value="normal">Normal charging</option>
                <option value="overnight">Overnight charging</option>
                <option value="overcharge">Frequent overcharging</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Device Age (months)</label>
              <input type="range" min="0" max="48" value={deviceAge} onChange={(e) => setDeviceAge(parseInt(e.target.value))} className="w-full" />
              <div className="text-right text-sm text-gray-600 mt-1">{deviceAge} months ({Math.round(deviceAge/12)} years)</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Heat Exposure Level</label>
              <select value={heatExposure} onChange={(e) => setHeatExposure(e.target.value)} className="w-full px-4 py-2.5 border rounded-lg">
                <option value="low">Low - Normal room temperature</option>
                <option value="medium">Medium - Gaming / Direct sunlight</option>
                <option value="high">High - Hot climate / Poor ventilation</option>
              </select>
            </div>
          </div>
        </InputCard>

        <div className="space-y-5">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="bg-white rounded-xl shadow-sm p-6 border text-center"
          >
            <div className="relative w-44 h-44 mx-auto mb-4">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="88" cy="88" r="76" stroke="#e5e7eb" strokeWidth="14" fill="none" />
                <circle 
                  cx="88" cy="88" r="76" 
                  stroke={batteryHealth >= 70 ? "#10b981" : batteryHealth >= 40 ? "#eab308" : "#ef4444"} 
                  strokeWidth="14" fill="none" 
                  strokeDasharray={`${(batteryHealth / 100) * 477.5} 477.5`} 
                  strokeLinecap="round"
                  className="transition-all duration-700"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-5xl font-bold text-gray-800">{batteryHealth}%</span>
                <span className="text-sm text-gray-500">Health</span>
              </div>
            </div>
            <div className={`inline-block px-4 py-1.5 rounded-full ${healthStatus.bg} ${healthStatus.text} font-semibold text-lg mb-3`}>
              {healthStatus.label} Battery
            </div>
            <p className="text-gray-600">Estimated lifespan remaining: <strong className="text-lg">{lifespanRemaining} months</strong></p>
            <p className="text-xs text-gray-400 mt-1">~{Math.round(lifespanRemaining/12)} years of optimal performance</p>
          </motion.div>
          
          <StatCard title="Charge Cycles Impact" value={Math.round(chargesPerDay * 365)} unit="cycles/year" icon={Activity} color="bg-orange-500" delay={0.3} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TipCard icon={Moon} title="Extend Battery Life" text="Avoid charging overnight - it degrades battery faster" color="blue" />
        <TipCard icon={Flame} title="Keep It Cool" text="Heat is the #1 enemy of battery health" color="orange" />
        <TipCard icon={Zap} title="Optimal Charging" text="Keep battery between 20% and 80% for longest life" color="green" />
        <TipCard icon={Clock} title="Monitor Health" text="Check battery health every 3 months" color="purple" />
      </div>
    </motion.div>
  );
};

export default BatteryHealthPredictor;